"use client";

import { track } from "@vercel/analytics";
import { format } from "date-fns-tz";
import { de } from "date-fns/locale";
import Link from "next/link";
import { useEffect, useState } from "react";

import { getEvents } from "@/lib/storyblok/getEvents";
import {
  collapseUpcomingEventsToRanges,
  EVENT_TIME_ZONE,
  getUpcomingEventQueries,
  type EventRange,
} from "@/lib/utils/events";

type EventTeaserProps = {
  campsiteId?: string;
  maxEvents?: number;
  title?: string;
};

const EventTeaser = ({
  campsiteId,
  maxEvents = 3,
  title = "Nächste Veranstaltungen",
}: EventTeaserProps) => {
  const [events, setEvents] = useState<EventRange[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      const now = new Date();
      const queryResults = await Promise.all(
        getUpcomingEventQueries(now, campsiteId).map((query) => getEvents(query))
      );

      setEvents(collapseUpcomingEventsToRanges(queryResults.flat(), now));
      setIsLoading(false);
    };

    fetchData();
  }, [campsiteId, maxEvents]);

  const formatEventRange = (startDate: Date, endDate: Date) => {
    const startLabel = format(startDate, "dd.MM.", {
      locale: de,
      timeZone: EVENT_TIME_ZONE,
    });

    if (startDate.getTime() === endDate.getTime()) {
      return startLabel;
    }

    return `${startLabel}-${format(endDate, "dd.MM.", {
      locale: de,
      timeZone: EVENT_TIME_ZONE,
    })}`;
  };

  return (
    <div className="flex flex-col gap-2 mt-4">
      {isLoading && <Skeleton number={maxEvents} />}

      {!isLoading && events.length > 0 && (
        <>
          <Link href="/veranstaltungen">
            <span className="font-semibold text-brand-primary100">{title}</span>
          </Link>
          <ul className="w-full text-sm">
            {events.slice(0, maxEvents).map(({ key, event, startDate, endDate }) => (
              <li key={key} className="px-2 py-2 border-b border-gray-200">
                <Link
                  href={event.full_slug}
                  onClick={() => track("Event click")}
                  className="flex flex-col gap-0.5"
                >
                  <time
                    className="font-medium text-gray-500"
                    dateTime={event.content?.date}
                  >
                    {formatEventRange(startDate, endDate)}
                  </time>
                  <span>{event.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const Skeleton = ({ number = 1 }) => (
  <>
    <div
      role="status"
      className="w-8/12 mb-1 h-6 bg-gray-200 animate-pulse"
    ></div>
    {Array(number)
      .fill(0)
      .map((_, index) => (
        <div
          key={index}
          className="w-12/12 my-1 h-4 bg-gray-200 animate-pulse"
        ></div>
      ))}
  </>
);

export { EventTeaser };
